/**
 * GUI kit: the shared "GUI pixel" foundation for every vanilla-look screen.
 *
 * A GuiStage's `content` div is innerWidth/S x innerHeight/S CSS px scaled up by
 * the integer GUI scale S, so absolute positions / sizes inside it are the exact
 * vanilla GUI-pixel numbers. Text is drawn from the real bitmap font
 * (mc/font/ascii.png) via `mcText`; widgets (McButton, McSlider, Tooltip) use the
 * real widget sprites. Everything degrades gracefully when a PNG is missing.
 *
 * Notes for users:
 *  - `loadKit()` must resolve before building UI (glyph widths come from the font).
 *  - `GuiStage.el` is interactive (pointer-events:auto) by default; HUD stages
 *    that must not eat clicks set `stage.interactive = false`.
 *  - McSlider renders "<label>: <format(v)>" (just format(v) when label is '').
 *  - Asset paths are relative to the `mc/` asset root ('gui/container/x.png').
 */

const MC = 'mc/';
const url = (p: string): string => (/^(https?:|data:|blob:|\/)/.test(p) ? p : p.startsWith('mc/') ? p : MC + p);

/** Scale of the most recently laid-out visible stage; the screen-px Tooltip uses it. */
let activeScale = 1;

export function guiScaleFor(setting: number): number {
  const w = typeof innerWidth === 'number' ? innerWidth : 1280;
  const h = typeof innerHeight === 'number' ? innerHeight : 720;
  let auto = 1;
  while (w / (auto + 1) >= 320 && h / (auto + 1) >= 240) auto++;
  if (setting >= 1) return Math.max(1, Math.min(auto, Math.floor(setting)));
  return auto;
}

// ---------------------------------------------------------------- font ----

const GLYPH = 8;
let fontImg: HTMLImageElement | null = null;
/** Glyph pixel width (excluding the 1px gap) per char code 0..255. */
const glyphW = new Uint8Array(256).fill(5);

function loadImage(path: string): Promise<HTMLImageElement | null> {
  return new Promise((res) => {
    const img = new Image();
    img.onload = () => res(img);
    img.onerror = () => res(null);
    img.src = url(path);
  });
}

const WIDGET_SPRITES = [
  'button', 'button_disabled', 'button_highlighted', 'slider', 'slider_highlighted',
  'slider_handle', 'slider_handle_highlighted', 'scroller', 'scroller_background',
  'text_field', 'text_field_highlighted', 'tab', 'tab_selected',
].map((n) => `gui/sprites/widget/${n}.png`);

let kitLoaded: Promise<void> | null = null;

export function loadKit(): Promise<void> {
  kitLoaded ??= (async () => {
    const [font] = await Promise.all([loadImage('font/ascii.png'), ...WIDGET_SPRITES.map(loadImage)]);
    fontImg = font;
    if (font) {
      const c = document.createElement('canvas');
      c.width = font.width; c.height = font.height;
      const g = c.getContext('2d', { willReadFrequently: true })!;
      g.drawImage(font, 0, 0);
      const d = g.getImageData(0, 0, c.width, c.height).data;
      const cell = Math.floor(font.width / 16); // 8 for ascii.png
      for (let code = 0; code < 256; code++) {
        const cx = (code & 15) * cell, cy = (code >> 4) * cell;
        let right = -1;
        for (let x = 0; x < cell; x++) {
          for (let y = 0; y < cell; y++) {
            if (d[((cy + y) * c.width + cx + x) * 4 + 3] > 0) { right = x; break; }
          }
        }
        // vanilla: width = rightmost lit column + 1; a blank glyph is the 4px space
        glyphW[code] = right < 0 ? 3 : right + 1;
      }
      glyphW[32] = 3;
    }
  })();
  return kitLoaded;
}

const codeOf = (ch: string): number => {
  const c = ch.charCodeAt(0);
  return c < 256 ? c : 63;
};

/** GUI px, vanilla glyph advance incl. the 1px gap (space = 4). */
export function textWidth(text: string): number {
  let w = 0;
  for (let i = 0; i < text.length; i++) w += glyphW[codeOf(text[i])] + 1;
  return w;
}

function shade(color: string, f: number): string {
  const c = document.createElement('canvas').getContext('2d')!;
  c.fillStyle = color;
  const m = /^#([0-9a-f]{6})$/i.exec(c.fillStyle as string);
  if (!m) return '#3f3f3f';
  const n = parseInt(m[1], 16);
  const r = ((n >> 16) & 255) * f, g = ((n >> 8) & 255) * f, b = (n & 255) * f;
  return `rgb(${r | 0},${g | 0},${b | 0})`;
}

const textCache = new Map<string, HTMLCanvasElement>();

/** Bitmap-font text. 1x canvas of (textWidth [+1 with shadow]) x (8 [+1]). */
export function mcText(text: string, opts: { color?: string; shadow?: boolean } = {}): HTMLCanvasElement {
  const color = opts.color ?? '#ffffff';
  const shadow = opts.shadow ?? false;
  const key = `${color}|${shadow ? 1 : 0}|${text}`;
  const hit = textCache.get(key);
  const out = document.createElement('canvas');
  const tw = Math.max(1, textWidth(text));
  out.width = tw + (shadow ? 1 : 0);
  out.height = GLYPH + (shadow ? 1 : 0);
  out.style.imageRendering = 'pixelated';
  const g = out.getContext('2d')!;
  g.imageSmoothingEnabled = false;
  if (hit) { g.drawImage(hit, 0, 0); return out; }

  // 1) white mask of the glyphs
  const mask = document.createElement('canvas');
  mask.width = tw; mask.height = GLYPH;
  const mg = mask.getContext('2d')!;
  mg.imageSmoothingEnabled = false;
  if (fontImg) {
    const cell = Math.floor(fontImg.width / 16);
    let x = 0;
    for (let i = 0; i < text.length; i++) {
      const code = codeOf(text[i]);
      const w = glyphW[code];
      if (code !== 32) mg.drawImage(fontImg, (code & 15) * cell, (code >> 4) * cell, w, cell, x, 0, w, GLYPH);
      x += w + 1;
    }
  } else {
    mg.fillStyle = '#fff';
    mg.font = '8px monospace';
    mg.textBaseline = 'top';
    mg.fillText(text, 0, 0);
  }
  // 2) tint helper
  const tint = (c: string): HTMLCanvasElement => {
    const t = document.createElement('canvas');
    t.width = tw; t.height = GLYPH;
    const tg = t.getContext('2d')!;
    tg.fillStyle = c;
    tg.fillRect(0, 0, tw, GLYPH);
    tg.globalCompositeOperation = 'destination-in';
    tg.drawImage(mask, 0, 0);
    return t;
  };
  if (shadow) g.drawImage(tint(shade(color, 0.25)), 1, 1);
  g.drawImage(tint(color), 0, 0);
  if (textCache.size > 800) textCache.clear();
  textCache.set(key, out);
  // return a fresh copy so callers may keep/mutate it
  const res = document.createElement('canvas');
  res.width = out.width; res.height = out.height;
  res.style.imageRendering = 'pixelated';
  res.getContext('2d')!.drawImage(out, 0, 0);
  return res;
}

export class Label {
  el: HTMLCanvasElement;
  private text: string;
  constructor(
    text: string, private x: number, private y: number,
    private opts: { color?: string; shadow?: boolean; align?: 'left' | 'center' | 'right' } = {},
  ) {
    this.text = text;
    this.el = document.createElement('canvas');
    this.el.style.cssText = 'position:absolute;image-rendering:pixelated;pointer-events:none;';
    this.paint();
  }
  set(text: string): void {
    if (text === this.text) return;
    this.text = text;
    this.paint();
  }
  setColor(c: string): void {
    if (this.opts.color === c) return;
    this.opts = { ...this.opts, color: c };
    this.paint();
  }
  private paint(): void {
    const src = mcText(this.text, this.opts);
    this.el.width = src.width; this.el.height = src.height;
    const g = this.el.getContext('2d')!;
    g.clearRect(0, 0, src.width, src.height);
    g.drawImage(src, 0, 0);
    const a = this.opts.align ?? 'left';
    const w = textWidth(this.text);
    const left = a === 'center' ? this.x - Math.floor(w / 2) : a === 'right' ? this.x - w : this.x;
    this.el.style.left = left + 'px';
    this.el.style.top = this.y + 'px';
    this.el.style.width = src.width + 'px';
    this.el.style.height = src.height + 'px';
  }
}

// ------------------------------------------------------------- sprites ----

const PIX = 'image-rendering:pixelated;image-rendering:crisp-edges;image-rendering:pixelated;';

export function sprite(path: string, x: number, y: number, w: number, h: number): HTMLDivElement {
  const d = document.createElement('div');
  d.style.cssText =
    `position:absolute;left:${x}px;top:${y}px;width:${w}px;height:${h}px;` +
    `background:url(${url(path)}) 0 0/100% 100% no-repeat;${PIX}pointer-events:none;`;
  return d;
}

/** A w x h window onto a sprite sheet at natural size (sx,sy = source origin). */
export function sheet(
  path: string, sx: number, sy: number, sw: number, sh: number, x: number, y: number,
  sheetW = 256, sheetH = 256,
): HTMLDivElement {
  const d = document.createElement('div');
  d.style.cssText =
    `position:absolute;left:${x}px;top:${y}px;width:${sw}px;height:${sh}px;` +
    `background:url(${url(path)}) ${-sx}px ${-sy}px/${sheetW}px ${sheetH}px no-repeat;${PIX}pointer-events:none;`;
  return d;
}

export function nineSlice(path: string, x: number, y: number, w: number, h: number, border: number): HTMLDivElement {
  const d = document.createElement('div');
  d.style.cssText =
    `position:absolute;left:${x}px;top:${y}px;width:${w}px;height:${h}px;box-sizing:border-box;` +
    `border:${border}px solid transparent;border-image:url(${url(path)}) ${border} fill / ${border}px stretch;${PIX}pointer-events:none;`;
  return d;
}

// ------------------------------------------------------------- stage ------

export class GuiStage {
  readonly el: HTMLDivElement;
  readonly content: HTMLDivElement;
  width = 320; height = 240; scale = 1;
  private setting = 0;
  private visible = true;
  private onResize = (): void => this.relayout();

  constructor(parent: HTMLElement, zIndex: number) {
    this.el = document.createElement('div');
    this.el.style.cssText = `position:fixed;inset:0;overflow:hidden;z-index:${zIndex};user-select:none;-webkit-user-select:none;`;
    this.content = document.createElement('div');
    this.content.style.cssText = `position:absolute;left:0;top:0;transform-origin:0 0;${PIX}`;
    this.el.appendChild(this.content);
    parent.appendChild(this.el);
    window.addEventListener('resize', this.onResize);
    this.relayout();
  }

  /** false => pointer-events:none (HUD stages). */
  set interactive(v: boolean) { this.el.style.pointerEvents = v ? 'auto' : 'none'; }

  setGuiScaleSetting(s: number): void { this.setting = s; this.relayout(); }

  relayout(): void {
    const S = guiScaleFor(this.setting);
    this.scale = S;
    this.width = Math.ceil(innerWidth / S);
    this.height = Math.ceil(innerHeight / S);
    this.content.style.width = this.width + 'px';
    this.content.style.height = this.height + 'px';
    this.content.style.transform = `scale(${S})`;
    if (this.visible) activeScale = S;
  }

  show(): void { this.visible = true; this.el.style.display = ''; this.relayout(); }
  hide(): void { this.visible = false; this.el.style.display = 'none'; }
  get shown(): boolean { return this.visible; }
}

// ------------------------------------------------------------- widgets ----

/** Button click sound hook (set by src/audio; kit stays audio-free). */
export const uiSound: { click: (() => void) | null; play: ((id: string) => void) | null } = { click: null, play: null };

export class McButton {
  el: HTMLDivElement;
  private bg: HTMLDivElement;
  private label: Label;
  private enabled = true;
  private hover = false;
  private text: string;
  constructor(label: string, x: number, y: number, w: number, h: number, onClick: () => void) {
    this.text = label;
    this.el = document.createElement('div');
    this.el.style.cssText = `position:absolute;left:${x}px;top:${y}px;width:${w}px;height:${h}px;cursor:pointer;`;
    this.bg = nineSlice('gui/sprites/widget/button.png', 0, 0, w, h, 3);
    this.label = new Label(label, Math.floor(w / 2), Math.floor((h - 8) / 2), { color: '#e0e0e0', shadow: true, align: 'center' });
    this.el.append(this.bg, this.label.el);
    this.el.addEventListener('mouseenter', () => { this.hover = true; this.paint(); });
    this.el.addEventListener('mouseleave', () => { this.hover = false; this.paint(); });
    this.el.addEventListener('click', (e) => { e.stopPropagation(); if (this.enabled) { uiSound.click?.(); onClick(); } });
    this.paint();
  }
  setLabel(s: string): void { this.text = s; this.paint(); }
  setEnabled(b: boolean): void { this.enabled = b; this.paint(); }
  private paint(): void {
    const state = !this.enabled ? 'button_disabled' : this.hover ? 'button_highlighted' : 'button';
    this.bg.style.borderImageSource = `url(${url(`gui/sprites/widget/${state}.png`)})`;
    this.el.style.cursor = this.enabled ? 'pointer' : 'default';
    this.label.setColor(!this.enabled ? '#a0a0a0' : this.hover ? '#ffffa0' : '#e0e0e0');
    this.label.set(this.text);
    this.el.dataset.label = this.text; // the label is drawn as a bitmap: this is how a test (or a screen reader shim) finds the button
  }
}

export class McSlider {
  el: HTMLDivElement;
  private bg: HTMLDivElement;
  private handle: HTMLDivElement;
  private label: Label;
  private v: number;
  private hover = false;
  private drag = false;
  constructor(
    private name: string, x: number, y: number, private w: number, value01: number,
    private format: (v01: number) => string, private onChange: (v01: number) => void,
  ) {
    this.v = Math.min(1, Math.max(0, value01));
    this.el = document.createElement('div');
    this.el.style.cssText = `position:absolute;left:${x}px;top:${y}px;width:${w}px;height:20px;cursor:pointer;`;
    this.bg = nineSlice('gui/sprites/widget/slider.png', 0, 0, w, 20, 3);
    this.handle = nineSlice('gui/sprites/widget/slider_handle.png', 0, 0, 8, 20, 3);
    this.label = new Label('', Math.floor(w / 2), 6, { color: '#e0e0e0', shadow: true, align: 'center' });
    this.el.append(this.bg, this.handle, this.label.el);
    this.el.addEventListener('mouseenter', () => { this.hover = true; this.paint(); });
    this.el.addEventListener('mouseleave', () => { this.hover = false; this.paint(); });
    this.el.addEventListener('pointerdown', (e) => {
      e.preventDefault(); e.stopPropagation();
      this.drag = true;
      this.el.setPointerCapture(e.pointerId);
      this.fromPointer(e);
    });
    this.el.addEventListener('pointermove', (e) => { if (this.drag) this.fromPointer(e); });
    const end = (): void => { this.drag = false; this.paint(); };
    this.el.addEventListener('pointerup', end);
    this.el.addEventListener('pointercancel', end);
    this.paint();
  }
  setValue(v01: number): void { this.v = Math.min(1, Math.max(0, v01)); this.paint(); }
  private fromPointer(e: PointerEvent): void {
    const r = this.el.getBoundingClientRect();
    const px = ((e.clientX - r.left) / r.width) * this.w;
    const v = Math.min(1, Math.max(0, (px - 4) / (this.w - 8)));
    if (v !== this.v) { this.v = v; this.onChange(v); }
    this.paint();
  }
  private paint(): void {
    const hl = this.hover || this.drag;
    this.bg.style.borderImageSource = `url(${url(`gui/sprites/widget/slider${hl ? '_highlighted' : ''}.png`)})`;
    this.handle.style.borderImageSource = `url(${url(`gui/sprites/widget/slider_handle${hl ? '_highlighted' : ''}.png`)})`;
    this.handle.style.left = Math.round(this.v * (this.w - 8)) + 'px';
    this.label.setColor(hl ? '#ffffa0' : '#e0e0e0');
    const f = this.format(this.v);
    this.label.set(this.name ? `${this.name}: ${f}` : f);
  }
}

// ------------------------------------------------------------- tooltip ----

let tipEl: HTMLDivElement | null = null;

export const Tooltip = {
  /** Vanilla item tooltip: first line white, extra lines grey; positioned in screen px. */
  show(lines: string[], clientX: number, clientY: number): void {
    if (lines.length === 0) { Tooltip.hide(); return; }
    if (!tipEl) {
      tipEl = document.createElement('div');
      tipEl.style.cssText = 'position:fixed;left:0;top:0;z-index:100000;pointer-events:none;transform-origin:0 0;image-rendering:pixelated;';
      document.body.appendChild(tipEl);
    }
    const S = activeScale;
    tipEl.replaceChildren();
    // a leading "§x" picks a vanilla chat colour for that line (§b aqua = enchanted name)
    const CODES: Record<string, string> = { b: '#55ffff', e: '#ffff55', c: '#ff5555', 7: '#aaaaaa', 9: '#5555ff', d: '#ff55ff', f: '#ffffff' };
    // "§o" before the colour code = italic (a renamed item)
    const italics = lines.map((l) => l.startsWith('\u00a7o'));
    lines = lines.map((l) => (l.startsWith('\u00a7o') ? l.slice(2) : l));
    const colors = lines.map((l, i) => (l[0] === '\u00a7' ? CODES[l[1]] ?? null : null) ?? (i === 0 ? '#ffffff' : '#aaaaaa'));
    lines = lines.map((l) => (l[0] === '\u00a7' ? l.slice(2) : l));
    let w = 0;
    for (const l of lines) w = Math.max(w, textWidth(l));
    const h = 8 + (lines.length - 1) * 10 + (lines.length > 1 ? 2 : 0);
    const box = document.createElement('div');
    // vanilla: 3px of padding (4 above) inside a 1px purple gradient frame over #100010 @ 94%
    box.style.cssText =
      `position:relative;box-sizing:content-box;width:${w}px;height:${h}px;padding:4px 3px 3px 3px;` +
      'background:rgba(16,0,16,0.94);';
    const frame = document.createElement('div');
    frame.style.cssText =
      'position:absolute;inset:1px;border:1px solid transparent;' +
      'border-image:linear-gradient(rgba(80,0,255,0.31),rgba(40,0,127,0.31)) 1;pointer-events:none;';
    box.appendChild(frame);
    lines.forEach((l, i) => {
      const c = mcText(l, { color: colors[i], shadow: true });
      c.style.cssText = `${italics[i] ? 'transform:skewX(-12deg);transform-origin:50% 100%;' : ''}position:absolute;left:3px;top:${4 + i * 10 + (i > 0 ? 2 : 0)}px;width:${c.width}px;height:${c.height}px;image-rendering:pixelated;`;
      box.appendChild(c);
    });
    tipEl.appendChild(box);
    tipEl.style.display = 'block';
    tipEl.style.transform = `scale(${S})`;
    const bw = (w + 6) * S, bh = (h + 7) * S;
    let px = clientX + 12 * S;
    let py = clientY - 12 * S;
    if (px + bw > innerWidth) px = Math.max(0, clientX - 12 * S - bw);
    if (py + bh > innerHeight) py = innerHeight - bh;
    if (py < 0) py = 0;
    tipEl.style.left = px + 'px';
    tipEl.style.top = py + 'px';
  },
  hide(): void { if (tipEl) tipEl.style.display = 'none'; },
};
